import { globalPokemonDataStore } from '../../OverworldAnimation.js';

class MoveSetManager {
    constructor() {
        this.store = {};
        this.apiLearnSets = {}; // Store API learn sets for comparison
        this.useAPI = true; // Flag to toggle between API and CSV methods (defaults to API)
        this.alwaysFetchFromAPI = false; // If true, always fetch from API and overwrite localStorage
        this.localStorageKey = 'pkmng_learn_sets'; // Key for storing learn sets in localStorage
    }

    /**
     * Transform full learnset data (with move objects) to minimal format (identifiers only)
     * This dramatically reduces storage size
     */
    compressLearnsetData(fullData) {
        const compressed = {};
        Object.keys(fullData).forEach((pokemonIdStr) => {
            const pokemonId = pokemonIdStr;
            const learnset = fullData[pokemonId];
            const compressedLearnset = {};

            // Compress levelUp: [{ level, move: {...} }] -> [{ level, moveIdentifier }]
            if (learnset.levelUp && Array.isArray(learnset.levelUp)) {
                compressedLearnset.levelUp = learnset.levelUp.map((entry) => ({
                    level: entry.level,
                    moveIdentifier: entry.move?.identifier || null,
                }));
            }

            // Compress tm/egg/tutor: [{ identifier, ... }] -> [identifier]
            if (learnset.tm && Array.isArray(learnset.tm)) {
                compressedLearnset.tm = learnset.tm.map((move) => move.identifier).filter((id) => id);
            } else {
                compressedLearnset.tm = [];
            }

            if (learnset.egg && Array.isArray(learnset.egg)) {
                compressedLearnset.egg = learnset.egg.map((move) => move.identifier).filter((id) => id);
            } else {
                compressedLearnset.egg = [];
            }

            if (learnset.tutor && Array.isArray(learnset.tutor)) {
                compressedLearnset.tutor = learnset.tutor.map((move) => move.identifier).filter((id) => id);
            } else {
                compressedLearnset.tutor = [];
            }

            compressed[pokemonId] = compressedLearnset;
        });
        return compressed;
    }

    saveToLocalStorage(data) {
        if (!data || typeof data !== 'object') {
            console.error('Invalid data provided to saveToLocalStorage:', typeof data);
            return;
        }

        try {
            const count = Object.keys(data).length;
            if (count === 0) {
                console.warn('No data to save to localStorage (empty object)');
                return;
            }

            // Compress data to only store identifiers (much smaller)
            const compressed = this.compressLearnsetData(data);
            const serialized = JSON.stringify(compressed);

            // Log size for debugging
            const sizeKB = (serialized.length / 1024).toFixed(2);
            console.log(`Compressed learnset data size: ${sizeKB} KB`);

            localStorage.setItem(this.localStorageKey, serialized);

            // Verify it was saved
            const verification = localStorage.getItem(this.localStorageKey);
            if (verification) {
                console.log(`Saved learn sets for ${count} Pokemon to localStorage (${sizeKB} KB)`);
            } else {
                console.error('Failed to verify localStorage save - data was not persisted');
            }
        } catch (error) {
            console.error(`Failed to save learn sets to localStorage: ${error}`, error);
            // If quota exceeded, try to clear old data or warn user
            if (error.name === 'QuotaExceededError') {
                console.warn('localStorage quota exceeded. Clearing old learnset data and retrying...');
                try {
                    localStorage.removeItem(this.localStorageKey);
                    // Retry with compressed data
                    const compressed = this.compressLearnsetData(data);
                    const serialized = JSON.stringify(compressed);
                    localStorage.setItem(this.localStorageKey, serialized);
                    console.log('Successfully saved after clearing old data');
                } catch (retryError) {
                    console.error('Failed to save even after clearing old data:', retryError);
                }
            }
        }
    }

    loadFromLocalStorage() {
        try {
            const cachedData = localStorage.getItem(this.localStorageKey);
            if (cachedData) {
                const parsed = JSON.parse(cachedData);
                const count = Object.keys(parsed).length;

                // Check if data is in compressed format (has moveIdentifier) or full format (has move object)
                // If compressed, we can use it directly since populateMoveSetsFromAPI handles both formats
                const sizeKB = (cachedData.length / 1024).toFixed(2);
                console.log(`Loaded learn sets for ${count} Pokemon from localStorage (${sizeKB} KB)`);
                return parsed;
            }
        } catch (error) {
            console.error(`Failed to load learn sets from localStorage: ${error}`);
        }
        return null;
    }

    async loadAllMoveSets() {
        try {
            if (this.useAPI) {
                // Check localStorage first if we're not forcing API fetch
                if (!this.alwaysFetchFromAPI) {
                    const cachedData = this.loadFromLocalStorage();
                    if (cachedData && Object.keys(cachedData).length > 0) {
                        this.apiLearnSets = cachedData;
                        this.populateMoveSetsFromAPI();
                        console.log(globalPokemonDataStore);
                        // Quick progress animation for localStorage load
                        const { animateQuickProgress } = await import('../../OverworldAnimation.js');
                        await animateQuickProgress(
                            'MOVESET_DATA',
                            `Loaded ${Object.keys(cachedData).length} learnsets from cache`
                        );
                        return;
                    }
                }

                // Fetch learn sets from API
                const { updateLoadingProgress } = await import('../../OverworldAnimation.js');
                updateLoadingProgress('MOVESET_DATA', 'Fetching learn sets from API...');
                await this.fetchLearnSetsFromAPI();

                // Verify we have data before saving
                if (this.apiLearnSets && Object.keys(this.apiLearnSets).length > 0) {
                    // Store in localStorage after fetching (ensures data is cached for next load)
                    this.saveToLocalStorage(this.apiLearnSets);
                } else {
                    console.warn('No learnset data to save to localStorage');
                }

                // Populate globalPokemonDataStore from API data
                this.populateMoveSetsFromAPI();
            } else {
                // Load from CSV file
                const { updateLoadingProgress } = await import('../../OverworldAnimation.js');
                updateLoadingProgress('MOVESET_DATA', 'Loading learn sets from CSV...');
                await this.loadMoveSetsFromCSV();
            }
        } catch (error) {
            console.error(`Failed to load movesets: ${error}`);
            console.error(`Error loading movesets: ${error.message}`);
            // Even if there's an error, try to save whatever data we have (if any)
            if (this.apiLearnSets && Object.keys(this.apiLearnSets).length > 0) {
                console.log('Attempting to save partial learnset data to localStorage after error...');
                this.saveToLocalStorage(this.apiLearnSets);
            }
        }
        console.log(globalPokemonDataStore);
    }

    async loadMoveSetsFromCSV() {
        try {
            // Load CSV data
            const response = await fetch('/data/learn_set_data.csv');
            const csvText = await response.text();
            const lines = csvText.split('\n').filter((line) => line.trim() !== '');

            // Parse CSV data and populate globalPokemonDataStore
            for (let i = 0; i < lines.length; i++) {
                const [pokemonId, level, attack] = lines[i].split(',');
                const pokemonIdNum = parseInt(pokemonId);

                // Populate globalPokemonDataStore for backward compatibility
                if (globalPokemonDataStore[pokemonIdNum]) {
                    globalPokemonDataStore[pokemonIdNum].moveSetByLevelUp[level] =
                        globalPokemonDataStore[pokemonIdNum].moveSetByLevelUp[level] || [];
                    globalPokemonDataStore[pokemonIdNum].moveSetByLevelUp[level].push(attack);
                    // Initialize other move types as empty arrays (no longer using backup JSON)
                    if (!globalPokemonDataStore[pokemonIdNum].moveSetByEgg) {
                        globalPokemonDataStore[pokemonIdNum].moveSetByEgg = [];
                    }
                    if (!globalPokemonDataStore[pokemonIdNum].moveSetByHM) {
                        globalPokemonDataStore[pokemonIdNum].moveSetByHM = [];
                    }
                    if (!globalPokemonDataStore[pokemonIdNum].moveSetByTM) {
                        globalPokemonDataStore[pokemonIdNum].moveSetByTM = [];
                    }
                    if (!globalPokemonDataStore[pokemonIdNum].moveSetByTutor) {
                        globalPokemonDataStore[pokemonIdNum].moveSetByTutor = [];
                    }
                }
            }
        } catch (error) {
            console.error(`Failed to load CSV movesets: ${error}`);
            throw error;
        }
    }

    async fetchLearnSetsFromAPI() {
        const API_BASE = import.meta.env.VITE_API_BASE || 'https://api.pkmng.orly.io/api';

        try {
            // Clear previous data
            this.apiLearnSets = {};

            // Fetching learn sets from API (progress handled by caller)
            const response = await fetch(`${API_BASE}/pokemon/game/learnsets`);
            if (!response.ok) {
                throw new Error(`Failed to fetch learn sets: ${response.status}`);
            }

            const responseData = await response.json();
            console.log('API response received:', {
                hasLearnSets: !!responseData.learnSets,
                isArray: Array.isArray(responseData.learnSets),
                count: responseData.learnSets?.length || 0,
            });

            // API returns { learnSets: [{ id, learnset: { levelUp, tm, egg, tutor } }] }
            // Store learn sets by pokemon ID
            if (responseData.learnSets && Array.isArray(responseData.learnSets)) {
                responseData.learnSets.forEach((pokemonData) => {
                    const pokemonId = pokemonData.id;
                    if (pokemonId && pokemonData.learnset) {
                        this.apiLearnSets[pokemonId] = pokemonData.learnset;
                    }
                });
            }

            const count = Object.keys(this.apiLearnSets).length;
            console.log(`Fetched learn sets for ${count} Pokemon from API`);
            console.log(`Fetched learn sets for ${count} Pokemon from API`);

            if (count === 0) {
                console.warn('Warning: No learnset data was fetched from API');
            }
        } catch (error) {
            console.error(`Error fetching learn sets from API: ${error.message}`);
            throw error;
        }
    }

    populateMoveSetsFromAPI() {
        // Populate globalPokemonDataStore from API learn sets
        Object.keys(this.apiLearnSets).forEach((pokemonIdStr) => {
            const pokemonId = parseInt(pokemonIdStr);
            const learnSet = this.apiLearnSets[pokemonId];

            if (!globalPokemonDataStore[pokemonId]) {
                console.warn(`Pokemon ${pokemonId} not found in globalPokemonDataStore`);
                return;
            }

            // Initialize moveSetByLevelUp if it doesn't exist
            if (!globalPokemonDataStore[pokemonId].moveSetByLevelUp) {
                globalPokemonDataStore[pokemonId].moveSetByLevelUp = {};
            }

            // Process level-up moves
            // Handles both formats:
            // - API format: [{ level: number, move: { identifier, ... } }]
            // - Compressed format: [{ level: number, moveIdentifier: string }]
            if (learnSet.levelUp && Array.isArray(learnSet.levelUp)) {
                learnSet.levelUp.forEach((entry) => {
                    const level = entry.level;
                    // Handle both formats: compressed (moveIdentifier) or full (move.identifier)
                    const moveIdentifier = entry.moveIdentifier || entry.move?.identifier;

                    if (level && moveIdentifier) {
                        // Initialize array for this level if it doesn't exist
                        if (!globalPokemonDataStore[pokemonId].moveSetByLevelUp[level]) {
                            globalPokemonDataStore[pokemonId].moveSetByLevelUp[level] = [];
                        }

                        // Add move identifier to the level array
                        globalPokemonDataStore[pokemonId].moveSetByLevelUp[level].push(moveIdentifier);
                    }
                });
            }

            // Process TM moves
            // Handles both formats:
            // - API format: [{ identifier, ... }] (array of objects)
            // - Compressed format: [identifier] (array of strings)
            if (learnSet.tm && Array.isArray(learnSet.tm)) {
                if (learnSet.tm.length > 0 && typeof learnSet.tm[0] === 'string') {
                    // Already compressed format (array of identifiers)
                    globalPokemonDataStore[pokemonId].moveSetByTM = learnSet.tm.filter((id) => id);
                } else {
                    // Full format (array of move objects)
                    globalPokemonDataStore[pokemonId].moveSetByTM = learnSet.tm
                        .map((move) => move.identifier)
                        .filter((id) => id);
                }
            } else {
                globalPokemonDataStore[pokemonId].moveSetByTM = [];
            }

            // Process egg moves (same dual format handling)
            if (learnSet.egg && Array.isArray(learnSet.egg)) {
                if (learnSet.egg.length > 0 && typeof learnSet.egg[0] === 'string') {
                    globalPokemonDataStore[pokemonId].moveSetByEgg = learnSet.egg.filter((id) => id);
                } else {
                    globalPokemonDataStore[pokemonId].moveSetByEgg = learnSet.egg
                        .map((move) => move.identifier)
                        .filter((id) => id);
                }
            } else {
                globalPokemonDataStore[pokemonId].moveSetByEgg = [];
            }

            // Process tutor moves (same dual format handling)
            if (learnSet.tutor && Array.isArray(learnSet.tutor)) {
                if (learnSet.tutor.length > 0 && typeof learnSet.tutor[0] === 'string') {
                    globalPokemonDataStore[pokemonId].moveSetByTutor = learnSet.tutor.filter((id) => id);
                } else {
                    globalPokemonDataStore[pokemonId].moveSetByTutor = learnSet.tutor
                        .map((move) => move.identifier)
                        .filter((id) => id);
                }
            } else {
                globalPokemonDataStore[pokemonId].moveSetByTutor = [];
            }

            // HM moves are merged into TM in the API, so set HM to empty array
            globalPokemonDataStore[pokemonId].moveSetByHM = [];
        });
    }

    populateMoveSetsFromBackup() {
        // Populate globalPokemonDataStore from backup data when API/localStorage/CSV loading fails
        Object.keys(this.store).forEach((pokemonIdStr) => {
            const pokemonId = parseInt(pokemonIdStr);
            const backupData = this.store[pokemonId];

            if (!globalPokemonDataStore[pokemonId]) {
                console.warn(`Pokemon ${pokemonId} not found in globalPokemonDataStore`);
                return;
            }

            // Initialize moveSetByLevelUp if it doesn't exist
            if (!globalPokemonDataStore[pokemonId].moveSetByLevelUp) {
                globalPokemonDataStore[pokemonId].moveSetByLevelUp = {};
            }

            // Populate level-up moves from backup data
            if (backupData.moveSetByLevelUp) {
                Object.keys(backupData.moveSetByLevelUp).forEach((level) => {
                    if (!globalPokemonDataStore[pokemonId].moveSetByLevelUp[level]) {
                        globalPokemonDataStore[pokemonId].moveSetByLevelUp[level] = [];
                    }
                    const moves = Array.isArray(backupData.moveSetByLevelUp[level])
                        ? backupData.moveSetByLevelUp[level]
                        : [backupData.moveSetByLevelUp[level]];
                    globalPokemonDataStore[pokemonId].moveSetByLevelUp[level].push(...moves);
                });
            }

            // Populate other move types from backup data
            globalPokemonDataStore[pokemonId].moveSetByEgg = backupData.moveSetByEgg || {};
            globalPokemonDataStore[pokemonId].moveSetByHM = backupData.moveSetByHM || {};
            globalPokemonDataStore[pokemonId].moveSetByTM = backupData.moveSetByTM || {};
            globalPokemonDataStore[pokemonId].moveSetByTutor = backupData.moveSetByTutor || {};
        });
    }
}

export const moveSetManager = new MoveSetManager();
